import React, { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import { translation } from "../../assets/translation";
import { commonPaddingLeft, commonPaddingRight, currentUrl, handleUrl, language } from "../../assets/function";
import CustomText from "../../customComponents/customText";
import { Colors } from "../../assets/colors";
import { Link } from "react-scroll";
import logo from "../../assets/images/logo.png";
import PropTypes from "prop-types";
let staticText = translation[language];
const pages = [
  staticText.pricing,
  staticText.aboutUs,
  staticText.signUp,
  staticText.blog,
  staticText.career,
];

const settings = ["Profile", "Account", "Dashboard", "Logout"];

function Navbar({ handleNavItem }) {
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const drawerWidth = "100%";
  const theme = createTheme({
    typography: {
      fontFamily: "Epilogue", // Use fallback fonts
      fontWeight: "600",
    },
  });

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ textAlign: "center" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "95%",
          marginTop: 1,
        }}
      >
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="end"
          onClick={handleDrawerToggle}
          sx={{
            ml: "auto", // This pushes the button to the right
            display: {},
            color: Colors.menuCloseColor,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <List sx={{
        width: {
          xs: '90%',   // Take up 90% width on mobile
          sm: '80%',   // 80% on tablet
          md: '70%',   // 70% on desktop
        },
        margin: '0 auto',  // Center the list
        paddingTop: {
          xs: 2,
          sm: 3,
          md: 4,
        },
      }}>
        {pages.map((item) => (
          <ListItem 
            key={item} 
            disablePadding
            sx={{
              mb: {
                xs: 1,    // Add spacing between items
                sm: 1.5,
                md: 2,
              }
            }}
          >
            <ListItemButton
              sx={styles.listItemBtn}
              onClick={(e) => {
                handleNavItem(item, e);
                setMobileOpen(false);
              }}
            >
              <ThemeProvider theme={theme}>
                <CustomText
                  desc={item}
                  fontFamily="Epilogue"
                  colorvalue={Colors.black}
                  style={styles.menuText}
                  fontWeight={600}
                  h5
                />
              </ThemeProvider>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <AppBar
      position="static"
      component="nav"
      sx={styles.appBarStyle}
      elevation={0}
    >
      <Container maxWidth="xl" sx={{}}>
        <Toolbar disableGutters>
          {/* Logo for desktop */}
          <Box sx={{ flexGrow: 1, display: {} }}>
            <Link
              to="/"
              onClick={(e) => {
                e.preventDefault();
                if (window.location.pathname !== "/") {
                  handleUrl(currentUrl, e);
                }
              }}
              style={{ cursor: "pointer" }}
            >
              <img
                style={styles.logoStyle}
                src={logo}
                alt="logo"
                loading="lazy"
              />
            </Link>
          </Box>

          {/* Mobile menu */}
          <Box
            sx={{
              flexGrow: 1,
              display: {
                xs: "flex",
                sm: "none",
                md: "none",
                lg: "none",
                xl: "none"
              },
              
            }}
          >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              size="large"
              onClick={handleDrawerToggle}
              sx={{ 
                ml: "auto", 
                display: {}, 
                color: Colors.grey,
                padding: { xs: '12px', md: '8px' },  // Bigger padding on mobile
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: '2.3rem', md: '1.5rem' }  // Bigger icon on mobile
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              variant="temporary"
              anchor="right"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
              sx={{
                display: { xs: "block" },
                "& .MuiDrawer-paper": {
                  boxSizing: "border-box",
                  width: drawerWidth,
                  backgroundColor: Colors.menuListBgColor,
                },
              }}
            >
              {drawer}
            </Drawer>
          </Box>
          {/* Desktop menu */}
          <Box
            sx={{
              flexGrow: 0,
              display: {
                xs: "none",
                sm: "flex",
                md: "flex",
                lg: "flex",
                xl: "flex"
              },
              gap: { sm: '8px', md: '16px', lg: '24px' }, // Add consistent gap between items
              alignItems: 'center', // Ensure vertical alignment
              justifyContent: 'flex-end', // Align items to the right
            }}
          >
            {pages.map((page) => (
              <Button
                key={page}
                onClick={(e) => {
                  handleNavItem(page, e);
                }}
                sx={{
                  ...styles.menuBtn,
                  minWidth: 'auto', // Prevent button from expanding too much
                  padding: '8px 12px', // Add consistent padding
                }}
              >
                <CustomText
                  desc={page}
                  fontFamily="Epilogue"
                  colorvalue={Colors.menuTextColor}
                  style={{
                    ...styles.menuText,
                    fontSize: {
                      xs:'1rem',
                      sm:'1rem',
                      md:'1rem',
                      lg:'1rem',
                      xl:'1rem'
                    },
                    whiteSpace: 'nowrap', // Prevent text wrapping
                  }}
                  fontWeight={600}
                  h5
                />
              </Button>
            ))}
          </Box>

          {/* User menu */}
          <Box sx={{ flexGrow: 0 }}>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <MenuItem key={setting} onClick={handleCloseUserMenu}>
                  <Typography textAlign="center">{setting}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default Navbar;

Navbar.propTypes = {
  handleNavItem: PropTypes.func,
};

const styles = {
  logoStyle: {
    width: "auto",
    height: "clamp(38px, 7.5vw, 110px)", // Smooth scaling between 28px and 110px
    borderRadius: "clamp(4px, 0.4vw, 7px)"
  },
  appBarStyle: {
    overflowX: "hidden", // Prevent horizontal scrolling
    boxSizing: "border-box",
    backgroundColor: Colors.themeColor,
    paddingTop: {
      xs: '2vh',    // Smaller padding for mobile
      sm: '2.5vh',  // Slightly larger for tablets
      md: '3vh',    // Medium size for small laptops
      lg: '3.5vh',  // Default size for laptops/desktops
      xl: '4vh',    // Extra large screens
    },
    paddingLeft: commonPaddingLeft,
    paddingRight: commonPaddingRight
  },
  menuText: {
    textTransform: "none",
    "&:hover": {
      color: Colors.menuTextColorHover,
    },
    fontSize: {
      xs: '1rem',      // 16px on mobile
      sm: '1rem',  // 18px on tablet
      md: '1rem',   // 20px on desktop
      xl: '1rem',
      lg: '1rem',
    },
    lineHeight: {
      xs: 1.4,
      sm: 1.5,
      md: 1.6,
    },
  },
  menuBtn: {
    backgroundColor: Colors.themeColor,
    marginLeft: 0, // Remove the margin since we're using gap
  },
  listItemBtn: {
    textAlign: "center",
    marginTop: {
      xs: 2,    // 16px on mobile
      sm: 3,    // 24px on tablet
      md: 4,    // 32px on desktop
    },
    marginBottom: {
      xs: 2,
      sm: 3,
      md: 4,
    },
    justifyContent: "center",
    padding: {
      xs: '8px 16px',
      sm: '12px 24px',
      md: '16px 32px',
    },
  },
};
